// React imports
import React, { useState, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';

// MUI imports
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import CircularProgress from '@mui/material/CircularProgress';
import { blue, green, red, grey } from '@mui/material/colors';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck, faTimes, faCoins, faUpload, faDiagramVenn, faRectangleHistory, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';

// Other imports
import _ from 'lodash';

// Local imports
import Header from '../../Header.js';
import TransportationMethod from '../../TransportationMethod.js';
import StyledDropzone from '../components/StyledDropzone.js';

import { 
    CreateImport as CreateCargofiveImport, 
    apiCreateRequest as apiCreateCargofiveRequest 
} from './RatesImport.cargofive.js';

import { 
    CreateImport as CreateMonadaImport, 
    apiCreateRequest as apiCreateMonadaRequest 
} from './RatesImport.monada.js';

import ImportLog from './RatesImport.log.js';
import FreeUploadTemplate from './RatesImport.freeupload.js';

dayjs.extend(duration);
dayjs.extend(relativeTime);

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
};

export default function RatesImport(props) {
    const { type } = props;
    const { org, accesstoken, me, unlocode, seaPorts } = useOutletContext();
    const navigate = useNavigate();
    const theme = useTheme();

    return (
        <Box>
            <Header
                icon={faCoins}
                title={'Rates'}
                tabs={_.compact([{
                    icon: faMagnifyingGlass,
                    title: 'Search',
                    action: () => navigate('../rates'),
                }, {
                    icon: faUpload,
                    title: 'Import',
                    action: () => navigate('../rates/import'),
                    selected: type === 'new'
                }, me.email !== 'admin@monada.ai' ? null : {
                    icon: faUpload,
                    title: 'Upload',
                    action: () => navigate('../rates/upload')
                }, {
                    icon: faDiagramVenn,
                    title: 'Overlapping',
                    action: () => navigate('../rates/overlapping')
                }])}
                noMarginBottom={true}
            />
            <Box sx={{ display: 'flex', height: '32px', background: theme.palette.panel1, alignItems: 'center', borderBottom: `1px solid ${theme.palette.border1}`, marginBottom: '12px' }}>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ flex: 0, display: 'flex', height: '32px', background: theme.palette.panel1, borderLeft: `1px solid ${theme.palette.border1}`, alignItems: 'center', padding: '0px 12px', borderBottom: type === 'log' ? `2px solid ${blue[500]}` : '' }} >
                    <Button sx={{ textTransform: 'none', fontSize: '13px' }} onClick={() => navigate('./log')} disabled={type === 'log'}>
                        <FontAwesomeIcon icon={faRectangleHistory} />
                        <span style={{ paddingLeft: '6px', whiteSpace: 'nowrap' }}>
                            Tariff Import History
                        </span>
                    </Button>
                </Box>
            </Box>
            {type === 'new' && (
                <>
                    <ImportNew org={org} accesstoken={accesstoken} />
                    <Box sx={{ p: 3, pt: 0 }}>
                        <FreeUploadTemplate org={org} accesstoken={accesstoken} me={me} />
                    </Box>
                </>
            )}
            {type === 'log' && <ImportLog org={org} me={me} accesstoken={accesstoken} unlocode={unlocode} seaPorts={seaPorts} />}
        </Box>
    );
}

function ImportNew(props) {
    const { org, accesstoken } = props;
    const [ open, setOpen ] = useState({ page: -1 });
    const [ errors, setErrors ] = useState([]);
    const [ file, setFile ] = useState(null);
    const [ uploading, setUploading ] = useState(false);
    const [ transportationMethod, setTransportationMethod ] = useState(null);
    const [ shipmentType, setShipmentType ] = useState(null);
    const [ parsingSupplierForm, setParsingSupplierForm ] = useState(null);

    // Ocean uploads go to CargoFive unless a Monada admin has switched that off for this
    // workspace, per shipment type (Settings > CargoFive import routing). Only when one of
    // the two is off does it matter whether a file is FCL or LCL, so only then do we ask.
    const cargofiveTakesFcl = _.get(org, 'integrations.cargofive.sendFclImports', true) !== false;
    const cargofiveTakesLcl = _.get(org, 'integrations.cargofive.sendLclImports', true) !== false;
    const askShipmentType = transportationMethod === 'sea' && !(cargofiveTakesFcl && cargofiveTakesLcl);
    const toCargofive = transportationMethod === 'sea' && (
        !askShipmentType || (shipmentType === 'FCL' ? cargofiveTakesFcl : shipmentType === 'LCL' ? cargofiveTakesLcl : false)
    );

    const onDrop = useCallback(acceptedFiles => {
        acceptedFiles.forEach((_file) => {
            const reader = new FileReader();

            reader.onabort = () => console.log('file reading was aborted');
            reader.onerror = () => console.log('file reading has failed');
            reader.onload = () => {
                // Do whatever you want with the file contents
                const dataurl = reader.result
                setFile({ file: _file, dataurl });
            };
            reader.readAsDataURL(_file);
        });
      }, [])

    function openImportDialog({ type, accept }) {
        setOpen({ page: 0, typeTitle: type, accept });
    }

    function handleBack() {
        setOpen({ ...open, page: open.page - 1 });
    }

    function handleClose() {
        setOpen({ ...open, page: -1 });
        setFile(null);
    }

    async function upload() {
        setUploading(true);

        try {
            if (toCargofive) {
                await apiCreateCargofiveRequest({ form: parsingSupplierForm, file: file.dataurl, fileName: file.file.originalname || file.file.name, org, accesstoken });
            } else {
                await apiCreateMonadaRequest({ form: parsingSupplierForm, file: file.dataurl, fileName: file.file.originalname || file.file.name, org, accesstoken, transportationMethod,
                    shipmentType: askShipmentType ? shipmentType : undefined });
            }

            setUploading(false);
            setOpen({ ...open, page: 10 });
        } catch (e) {
            setErrors(['upload']);
            setUploading(false);
        }
    }

    function onContinueToFirst() {
        setOpen({ ...open, page: 1 });
    }

    function onContinueToSecond({ form }) {
        setParsingSupplierForm(form);
        setOpen({ ...open, page: 2 });
    }

    return (
        <Box>
            <Box sx={{ p: 3 }}>
                <Box sx={{ alignItems: 'center', marginBottom: '24px', flex: 1, display: { xs: 'none', md: 'flex' }}}>
                    <Typography variant='h6' sx={{ flex: 1 }}>
                        Import Rates Data
                    </Typography>
                </Box>
                <Typography variant='body' sx={{ flex: 1 }}>
                    Import a new rates list - Price customers with rates from multiple sources.
                </Typography>
                <Typography variant='caption' sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                    Imported files count towards your organization's tariff list quota. For details about your plan, contact <a href='mailto:help@monada.ai' style={{ color: '#3b82f6' }}>help@monada.ai</a>.
                </Typography>
            </Box>
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-around', gap: 4 }}>
                <Button
                    onClick={() => openImportDialog({ type: 'Excel', accept: { 'application/vnd.ms-excel': [ '.xls', '.xlsx' ]} })}
                    sx={{
                        textTransform: 'none',
                        minWidth: 170,
                        minHeight: 170,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        border: 2,
                        borderColor: '#217346',
                        borderRadius: 4,
                        background: '#e2efe6',
                        '&:hover': { background: '#cbecd8', borderColor: '#155a37' }
                    }}
                >
                    <Box sx={{ fontSize: 56, fontWeight: 800, color: '#217346', mb: 1 }}>
                        Excel
                    </Box>
                    <Box sx={{ fontWeight: 500, color: '#217346' }}>
                        .xls / .xlsx
                    </Box>
                </Button>
                <Button
                    onClick={() => openImportDialog({ type: 'PDF', accept: { 'application/pdf': [ '.pdf' ]} })}
                    sx={{
                        textTransform: 'none',
                        minWidth: 170,
                        minHeight: 170,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        border: 2,
                        borderColor: '#be0a09',
                        borderRadius: 4,
                        background: '#ffecec',
                        '&:hover': { background: '#ffd5d5', borderColor: '#8d0000' }
                    }}
                >
                    <Box sx={{ fontSize: 56, fontWeight: 800, color: '#be0a09', mb: 1 }}>
                        PDF
                    </Box>
                    <Box sx={{ fontWeight: 500, color: '#be0a09' }}>
                        .pdf
                    </Box>
                </Button>
                <Button
                    onClick={() => openImportDialog({ type: 'CSV', accept: { 'text/csv': [ '.csv' ]} })}
                    sx={{
                        textTransform: 'none',
                        minWidth: 170,
                        minHeight: 170,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        border: 2,
                        borderColor: '#1877c9',
                        borderRadius: 4,
                        background: '#e3f1fd',
                        '&:hover': { background: '#d1e8fc', borderColor: '#0d4479' }
                    }}
                >
                    <Box sx={{ fontSize: 56, fontWeight: 800, color: '#1877c9', mb: 1 }}>
                        CSV
                    </Box>
                    <Box sx={{ fontWeight: 500, color: '#1877c9' }}>
                        .csv
                    </Box>
                </Button>
            </Box>

            <Modal open={open.page === 0} onClose={handleClose} >
                <Card sx={{ ...style, width: '560px'}}>
                    <CardContent>
                        <Typography gutterBottom variant='h5' component='div'>
                            Get your rates from <b>{open.typeTitle}</b>
                        </Typography>
                        <TransportationMethod
                            transportationMethod={transportationMethod}
                            setTransportationMethod={setTransportationMethod}
                        />
                        {askShipmentType && (
                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant='body2' sx={{ fontWeight: 600 }}>Is this file FCL or LCL?</Typography>
                                <ToggleButtonGroup exclusive size='small' value={shipmentType} onChange={(e, v) => v && setShipmentType(v)}
                                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 3 } }}>
                                    <ToggleButton value='FCL'>FCL (containers)</ToggleButton>
                                    <ToggleButton value='LCL'>LCL (by cbm / ton)</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        )}
                    </CardContent>
                    <CardActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button size='small' color='secondary' onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button size='small' color='primary' variant='contained' onClick={onContinueToFirst} disabled={!transportationMethod || (askShipmentType && !shipmentType)}>
                            Continue
                        </Button>
                    </CardActions>
                </Card>
            </Modal>

            <Modal open={open.page === 1} onClose={handleClose} >
                <Box>
                    {toCargofive && <CreateCargofiveImport
                        onNext={onContinueToSecond}
                        onBack={handleBack}
                        typeTitle={open.typeTitle}
                        form={parsingSupplierForm}
                        organizationId={org.id}
                        accesstoken={accesstoken}
                    />}
                    {transportationMethod && !toCargofive && <CreateMonadaImport
                        onNext={onContinueToSecond}
                        onBack={handleBack}
                        typeTitle={open.typeTitle}
                        form={parsingSupplierForm}
                        organizationId={org.id}
                        accesstoken={accesstoken}
                    />}
                </Box>
            </Modal>

            <Modal open={open.page === 2} onClose={handleClose} >
                <Card sx={{ ...style, width: '560px'}}>
                    <CardContent>
                        <Typography gutterBottom variant='h5' component='div'>
                            Get your rates from <b>{open.typeTitle}</b>
                        </Typography>
                        {!file && (<StyledDropzone onDrop={onDrop} accept={open.accept} />)}
                        {file && (
                            <div
                                style={{
                                    padding: '20px', borderWidth: 2, borderRadius: 2, borderColor: '#eeeeee',
                                    borderStyle: 'dashed', backgroundColor: '#fafafa', display: 'flex', alignItems: 'center'
                                }}
                            >
                                <Typography variant='caption' sx={{flex: 1, paddingLeft: '12px'}}>
                                    {file.file.name}
                                </Typography>
                                <Button variant='text' color='secondary' onClick={() => setFile(null)}><FontAwesomeIcon icon={faTimes}/></Button>
                            </div>
                        )}
                        {errors.includes('upload') && (
                            <Typography variant='caption' sx={{ color: red[500] }}>
                                Error uploading file
                            </Typography>
                        )}
                    </CardContent>
                    <CardActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button size='small' color='secondary' onClick={handleBack} disabled={uploading}>
                            Back
                        </Button>
                        <Button size='small' color='primary' sx={{ width: '200px' }} variant='contained' onClick={() => upload()} disabled={uploading || !(file || {}).file}>
                            {uploading && <CircularProgress size={18} />}
                            {!uploading && <span>Upload request</span>}
                        </Button>
                    </CardActions>
                </Card>
            </Modal>

            <Modal open={open.page === 10} onClose={handleClose} >
                <Card sx={{ ...style, width: '560px'}}>
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                        <Typography variant='h2' sx={{ color: green[500] }}>
                            <FontAwesomeIcon icon={faCircleCheck} />
                        </Typography>
                        <Typography variant='h4'>
                            Yay! Upload complete!
                        </Typography>
                        <Typography variant='body1' sx={{ paddingTop: '12px' }}>
                            We'll notify you as soon as the import is completed.
                        </Typography>
                    </CardContent>
                    <CardActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button size='small' color='primary' variant='contained' onClick={handleClose}>
                            Done
                        </Button>
                    </CardActions>
                </Card>
            </Modal>

        </Box>
    );
}